import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, DialogContent, Field, Input, ToggleGroup, ToggleGroupItem } from '@datanomik/core-ui';
import { Key2Line, MailLine } from '@datanomik/core-ui/icons';

import type { PasswordResetChannel } from '../api';
import { usePasswordReset } from '../hooks/usePasswordReset';
import { isWhatsAppResetEnabled } from '../lib/passwordResetChannels';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PasswordResetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PasswordResetDialog({ open, onOpenChange }: PasswordResetDialogProps) {
  const { t } = useTranslation();
  const { busy, error, sent, submit, reset } = usePasswordReset();
  const [channel, setChannel] = useState<PasswordResetChannel>('EMAIL');
  const whatsAppEnabled = isWhatsAppResetEnabled();
  const byWhatsApp = whatsAppEnabled && channel === 'WHATSAPP';
  const { register, handleSubmit, formState } = useForm<{ email: string }>({
    defaultValues: { email: '' },
  });

  const close = (next: boolean) => {
    if (!next) {
      reset();
      setChannel('EMAIL');
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        icon={<Key2Line />}
        title={t('Forgot your password?')}
        description={byWhatsApp
          ? t('Enter your email and we will send the link by WhatsApp to the mobile you verified in your profile.')
          : t('Enter your email and we will send you a link to choose a new one.')}
        footer={sent
          ? <Button className="w-full" onClick={() => close(false)}>{t('Close')}</Button>
          : (
            <Button type="submit" form="password-reset-form" className="w-full" loading={busy}>
              {t('Send me a reset link')}
            </Button>
          )}
      >
        {sent ? (
          <Alert tone="success" title={t('Reset link sent!')}>
            {byWhatsApp
              ? t('Help is on the way, please check your WhatsApp. If your mobile is not verified, we will send it to your email.')
              : t('Help is on the way, please check your email for further instructions.')}
          </Alert>
        ) : (
          <form
            id="password-reset-form"
            onSubmit={handleSubmit(({ email }) => submit(email, byWhatsApp ? 'WHATSAPP' : 'EMAIL'))}
            noValidate
            className="flex flex-col gap-3"
          >
            {error && <Alert tone="destructive">{error}</Alert>}
            {whatsAppEnabled && (
              <Field label={t('Send the link by')}>
                <ToggleGroup
                  aria-label={t('Send the link by')}
                  value={channel}
                  onValueChange={(next) => setChannel(next as PasswordResetChannel)}
                  className="w-full"
                >
                  <ToggleGroupItem value="EMAIL" className="flex-1">{t('Email')}</ToggleGroupItem>
                  <ToggleGroupItem value="WHATSAPP" className="flex-1">WhatsApp</ToggleGroupItem>
                </ToggleGroup>
              </Field>
            )}
            <Field label={t('Email')} required error={formState.errors.email?.message}>
              <Input
                type="email"
                autoComplete="email"
                placeholder={t('you@company.com')}
                startAdornment={<MailLine aria-hidden="true" />}
                {...register('email', {
                  required: t('Email is required'),
                  pattern: { value: EMAIL_PATTERN, message: t('Please enter a valid email address') },
                })}
              />
            </Field>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
